import React, { useState } from "react";
import DashboardPanel from "./DashboardPanel";

const fmtN = (n) => Number(n || 0).toLocaleString();
const CHART_HEIGHT = 128;

/**
 * Single-series column chart of the last few days. Each column is a full-height
 * hover target with a tooltip; the peak and today's values are labelled.
 */
function DailyColumns({ series, unit }) {
  const [hovered, setHovered] = useState(null);
  const max = Math.max(...series.map((d) => d.value), 0);
  const peakKey = max > 0 ? series.find((d) => d.value === max)?.key : null;

  // The columns are visual only; the table below carries the values for
  // screen readers.
  return (
    <div>
      <div className="relative" style={{ height: CHART_HEIGHT + 20 }}>
        {/* Recessive guides at 0 / 50% / 100% of the peak. */}
        {[0, 0.5, 1].map((f) => (
          <div
            key={f}
            className="pointer-events-none absolute inset-x-0 border-t border-dashed border-gray-100 dark:border-slate-700/70"
            style={{ bottom: 20 + f * CHART_HEIGHT }}
          />
        ))}
        <div className="absolute inset-x-0 bottom-[20px] border-t border-gray-200 dark:border-slate-600" />

        <div className="absolute inset-0 flex items-end gap-2">
          {series.map((d) => {
            const h = max ? Math.max((d.value / max) * CHART_HEIGHT, d.value ? 3 : 0) : 0;
            const showLabel = d.value > 0 && (d.key === peakKey || d.isToday);
            const active = hovered === d.key;
            return (
              <div
                key={d.key}
                className="relative flex h-full flex-1 flex-col items-center justify-end"
                onMouseEnter={() => setHovered(d.key)}
                onMouseLeave={() => setHovered(null)}
                aria-hidden="true"
              >
                {active && (
                  <div className="pointer-events-none absolute bottom-full z-10 mb-1 whitespace-nowrap rounded-md border border-app-primary bg-app-card px-2.5 py-1.5 text-xs shadow-md">
                    <div className="text-app-muted">{d.fullLabel}</div>
                    <div className="font-semibold text-app-primary">
                      {fmtN(d.value)} {unit}
                    </div>
                  </div>
                )}
                {showLabel && !active && (
                  <span className="mb-1 text-[11px] font-semibold tabular-nums text-app-secondary">
                    {fmtN(d.value)}
                  </span>
                )}
                <div
                  className={`w-full max-w-[28px] rounded-t-[4px] bg-blue-500 transition-opacity dark:bg-sky-400 ${
                    hovered && !active ? "opacity-50" : ""
                  }`}
                  style={{ height: h }}
                />
                <span
                  className={`mt-1 h-[16px] text-[11px] ${
                    d.isToday ? "font-semibold text-app-primary" : "text-app-muted"
                  }`}
                >
                  {d.isToday ? "Today" : d.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Table view for screen readers. */}
      <table className="sr-only">
        <caption>Daily {unit}</caption>
        <tbody>
          {series.map((d) => (
            <tr key={d.key}>
              <th scope="row">{d.fullLabel}</th>
              <td>{d.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 7-day totals per logged metric, as single-hue horizontal bars.
function MetricTotals({ totals }) {
  const max = Math.max(...totals.map((t) => t.value), 0);
  return (
    <ul className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
      {totals.map((t) => (
        <li key={t.key}>
          <div className="mb-1 flex items-center justify-between gap-2 text-xs">
            <span className="truncate text-app-secondary">{t.label}</span>
            <span className="font-semibold tabular-nums text-app-primary">{fmtN(t.value)}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
            <div
              className="h-full rounded-full bg-blue-500 dark:bg-sky-400"
              style={{ width: `${max ? (t.value / max) * 100 : 0}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Skeleton() {
  return (
    <div className="space-y-5" aria-hidden="true">
      <div className="flex h-[148px] items-end gap-2">
        {[40, 70, 30, 90, 55, 20, 65].map((h, i) => (
          <div key={i} className="flex-1 animate-pulse rounded-t bg-gray-100 dark:bg-slate-700/60" style={{ height: `${h}%` }} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-6 animate-pulse rounded bg-gray-100 dark:bg-slate-700/60" />
        ))}
      </div>
    </div>
  );
}

/**
 * Last-7-days activity: a daily column chart plus per-metric totals.
 * @param {{ title: string, description?: string, unit: string, data?: object,
 *           loading?: boolean, error?: boolean, emptyText?: string, footnote?: string }} props
 */
function ActivityTrendCard({ title, description, unit, data, loading = false, error = false, emptyText, footnote }) {
  let body;
  if (loading) body = <Skeleton />;
  else if (error) body = <p className="text-sm text-app-error">{"Couldn't load activity logs."}</p>;
  else {
    const hasActivity = data.totals.some((t) => t.value > 0) || data.daily.some((d) => d.value > 0);
    body = hasActivity ? (
      <div className="space-y-5">
        <DailyColumns series={data.daily} unit={unit} />
        <div>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-app-muted">
            7-day totals
          </h4>
          <MetricTotals totals={data.totals} />
        </div>
      </div>
    ) : (
      <p className="text-sm text-app-muted">{emptyText}</p>
    );
  }

  return (
    <DashboardPanel title={title} description={description}>
      {body}
      {footnote && !loading && !error && <p className="mt-4 text-[11px] text-app-muted">{footnote}</p>}
    </DashboardPanel>
  );
}

export default ActivityTrendCard;
